///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Decides how a tracked object looks on the globe from what the TLE feed
/// knows about it: its CelesTrak group memberships, its catalog name and
/// its mean motion. Shape carries the kind of object (a station truss, a
/// GEO comsat dish, a Starlink flat-sat, a debris shard), the ember dome
/// tint keeps carrying the orbit regime, and the object class sets how big
/// and how solid the far-mode dot is so debris clouds read as dust rather
/// than thousands of equal satellites.
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { type color } from "@stovokor/math/color"

/** Silhouette family drawn by SatelliteMarkerGeometry */
export type SatelliteArchetype =
	| "wings"     // bus with two solar wings: the generic LEO payload
	| "dish"      // bus, wings and a nadir-facing dish: GEO comms and weather
	| "flat"      // flat chassis with one wing: Starlink / OneWeb flat-sats
	| "nav"       // wings plus a nadir antenna array: GNSS
	| "tube"      // long cylinder with small wings: space telescopes
	| "cube"      // 3U stick with side panels: CubeSats, amateur radio
	| "truss"     // long truss with panel pairs and a module: stations
	| "cylinder"  // bare cylinder: spent rocket bodies
	| "shard"     // irregular fragment: debris

export type SatelliteClass = "station" | "payload" | "rocketBody" | "debris"

export interface SatelliteAppearance {
	archetype: SatelliteArchetype
	objectClass: SatelliteClass
	/** Ember dome tint (orbit regime, or debris grey) */
	color: color
	/** Dome radius and archetype unit size in km at view scale 1 */
	radiusKm: number
	/** Far-mode dot opacity (EmberDomeNode.farFillAlpha) */
	farFill: number
	/** Screen-space size floor in radians (EmberDomeNode.minAngularSize) */
	minAngular: number
}

const cStationGroups = new Set(["stations"])
const cFlatSatGroups = new Set(["starlink", "oneweb"])
const cNavGroups = new Set(["gnss", "gps-ops", "glo-ops", "galileo", "beidou", "sbas", "nnss", "musson"])
const cCubeGroups = new Set(["cubesat", "amateur", "satnogs", "education"])
const cDishGroups = new Set(["geo", "gpz", "gpz-plus", "ses", "intelsat", "eutelsat", "tdrss", "goes", "other-comm", "x-comm"])
const cDebrisGroups = new Set(["fengyun-1c-debris", "cosmos-2251-debris", "iridium-33-debris", "cosmos-1408-debris"])

// Observatories whose long optical / X-ray tube is their silhouette
const cTelescopeNames = /\b(HST|HUBBLE|XMM|INTEGRAL|CHANDRA|SWIFT|FERMI|NUSTAR|TESS|IXPE|XRISM|SPEKTR|EINSTEIN PROBE|SVOM)\b/
const cFlatSatNames = /^(STARLINK|ONEWEB)/
const cNavNames = /^(NAVSTAR|GPS |GLONASS|GALILEO|BEIDOU|QZS|IRNSS|NVS)/
const cStationNames = /^(ISS|TIANGONG|CSS \(|ZARYA)/
const cDebrisNames = /\bDEB\b|\bDEBRIS\b/
const cRocketBodyNames = /\bR\/B\b|\bAKM\b|\bPKM\b|ROCKET BODY/

// Class sizing: stations biggest and most solid, debris smallest and
// faintest. Payload values are the previous single-size satellite marker
const cClassSize: Record<SatelliteClass, { radiusKm: number, farFill: number, minAngular: number }> = {
	station: { radiusKm: 14, farFill: 0.85, minAngular: 0.0019 },
	payload: { radiusKm: 10, farFill: 0.7, minAngular: 0.0015 },
	rocketBody: { radiusKm: 7, farFill: 0.45, minAngular: 0.0011 },
	debris: { radiusKm: 5, farFill: 0.35, minAngular: 0.0009 },
}

/**
 * Marker tint by orbit class, derived from mean motion (revs/day):
 * geosynchronous ≈ 1 rev/day (gold), LEO ≥ ~11.25 revs/day i.e. periods
 * under ~128 min (cyan), everything between reads as MEO/HEO (blue)
 */
export function satelliteOrbitColor(meanMotion: number): color {
	if (meanMotion > 0.9 && meanMotion < 1.1) {
		return DesignColors.MARKER_SATELLITE_GEO
	}
	if (meanMotion >= 11.25) {
		return DesignColors.MARKER_SATELLITE_LEO
	}
	return DesignColors.MARKER_SATELLITE_MEO
}

function isGeosynchronous(meanMotion: number): boolean {
	return meanMotion > 0.9 && meanMotion < 1.1
}

function hasAny(groupIds: readonly string[], wanted: Set<string>): boolean {
	for (const id of groupIds) {
		if (wanted.has(id)) return true
	}
	return false
}

/**
 * Classify one catalog object. Group membership is the strongest signal
 * (the groups are curated by CelesTrak); names fill in for objects that
 * only appear in broad groups like "active" or "visual".
 */
export function classifySatellite(objectName: string, groupIds: readonly string[], meanMotion: number): SatelliteAppearance {
	const name = objectName.toUpperCase()
	let archetype: SatelliteArchetype
	let objectClass: SatelliteClass = "payload"

	if (cDebrisNames.test(name) || hasAny(groupIds, cDebrisGroups)) {
		archetype = "shard"
		objectClass = "debris"
	} else if (cRocketBodyNames.test(name)) {
		archetype = "cylinder"
		objectClass = "rocketBody"
	} else if (cStationNames.test(name) || hasAny(groupIds, cStationGroups)) {
		archetype = "truss"
		objectClass = "station"
	} else if (cFlatSatNames.test(name) || hasAny(groupIds, cFlatSatGroups)) {
		archetype = "flat"
	} else if (cTelescopeNames.test(name)) {
		archetype = "tube"
	} else if (cNavNames.test(name) || hasAny(groupIds, cNavGroups)) {
		archetype = "nav"
	} else if (hasAny(groupIds, cCubeGroups)) {
		archetype = "cube"
	} else if (isGeosynchronous(meanMotion) || hasAny(groupIds, cDishGroups)) {
		// Geostationary platforms (comms, GOES / Himawari / Meteosat) all
		// carry a big Earth-facing dish or imager
		archetype = "dish"
	} else {
		archetype = "wings"
	}

	const size = cClassSize[objectClass]
	const color = objectClass === "debris" || objectClass === "rocketBody"
		? DesignColors.MARKER_SATELLITE_DEBRIS
		: satelliteOrbitColor(meanMotion)

	return { archetype, objectClass, color, ...size }
}
